import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';

export type CustomerRateQuoteStatus =
  | 'rated'
  | 'no_rate_found'
  | 'needs_location_confirmation'
  | 'location_not_found'
  | 'needs_zone_selection'
  | 'zone_not_found';

export interface CustomerRateQuoteRequest {
  external_reference_number?: string;
  origin: RateLocation | string;
  destination: RateLocation | string;
  customer_contact_uuid?: string;
  quote_date?: string;
  pickup_date?: string;
  origin_zone_uuid?: string;
  destination_zone_uuid?: string;
  freight: RateFreight;
  freight_items?: RateFreightItem[];
}

export type RateFreightItem = CatalogRateFreightItem | ManualRateFreightItem;

export interface CatalogRateFreightItem {
  source: 'catalog';
  customer_product_uuid: string;
  package_quantity: number;
  floor_rotation_allowed?: boolean;
}

export interface ManualRateFreightItem {
  source: 'manual';
  label?: string;
  package_quantity: number;
  length: number;
  width: number;
  height: number;
  weight?: number;
  measurement_unit: 'IN' | 'CM';
  weight_unit: 'LB' | 'KG';
  stackable: boolean;
  floor_rotation_allowed: boolean;
}

export interface RateLocation {
  postal_code?: string;
  zip?: string;
  postal?: string;
  city?: string;
  locality?: string;
  state?: string;
  province?: string;
  region?: string;
  country?: string;
  lat?: number;
  lng?: number;
  latitude?: number;
  longitude?: number;
  contact_uuid?: string;
  contact_address_uuid?: string;
}

export interface RateFreight {
  total_weight_lb?: number;
  weight_lb?: number;
  linear_feet?: number;
  handling_units?: number;
  cubic_feet?: number;
  density_lb_per_cuft?: number;
  delivery_type?: number | null;
  preferred_rate_method?: 1 | 2 | 3 | 4;
  load_type?: string;
  route_distance_meters?: number;
  rated_miles?: number;
  route_duration_text?: string;
  route_static_duration_text?: string;
}

export interface CustomerShippingTargetAddress {
  contact_address_uuid: string | null;
  contact_uuid: string | null;
  address_type: string | null;
  address_label: string | null;
  address_primary: number | null;
  address_street: string | null;
  address_extended: string | null;
  address_locality: string | null;
  address_region: string | null;
  address_postal_code: string | null;
  address_country: string | null;
  lat: number | null;
  lng: number | null;
}

export interface CustomerShippingTargetContact {
  contact_uuid: string | null;
  display_name: string | null;
  contact_organization?: string | null;
  legal_name?: string | null;
  contact_name_given?: string | null;
  contact_name_family?: string | null;
}

export interface CustomerShippingTarget {
  relationship_uuid: string | null;
  relationship_type: 'shipping_origin' | string | null;
  created: string | null;
  target_contact_uuid: string | null;
  display_name: string | null;
  target: CustomerShippingTargetContact;
  default_address: CustomerShippingTargetAddress | null;
  addresses: CustomerShippingTargetAddress[];
  origin_location: RateLocation;
}

export interface CustomerShippingTargetsResponse {
  ok: boolean;
  resource: 'customer_shipping_targets';
  relationship_type: 'shipping_origin';
  source_contact_uuid: string;
  customer: CustomerShippingTargetContact;
  data: CustomerShippingTarget[];
  meta: {
    source: 'v_contact_relationships';
    scope: 'authenticated_customer';
    returned: number;
  };
}

export interface ResolvedLocation extends RateLocation {
  display_name?: string | null;
  formatted_address?: string | null;
  normalized?: string | null;
  candidates?: LocationCandidate[];
}

export interface LocationCandidate extends RateLocation {
  label?: string | null;
  display_name?: string | null;
  formatted_address?: string | null;
  confidence?: number | null;
}

export interface ZoneResolution {
  zone_uuid?: string | null;
  zone_name?: string | null;
  name?: string | null;
  zone_id?: string | null;
  matches?: CustomerRateZoneMatch[];
}

export interface CustomerRateZoneMatch {
  zone_uuid: string;
  zone_name?: string | null;
  name?: string | null;
  zone_id?: string | null;
  country?: string | null;
  confidence?: number | null;
}

export interface CustomerRateMoney {
  amount_cents: number;
  amount: number;
  currency: string;
  money_scale: 'cents';
}

export interface CustomerRateLineItem {
  label?: string | null;
  name?: string | null;
  description?: string | null;
  amount?: number | null;
  amount_cents?: number | null;
  currency?: string | null;
  total?: CustomerRateMoney | null;
}

export interface CustomerQuoteCondition {
  code: string;
  title: string;
  text: string;
  required: boolean;
}

export interface CreateCurrentQuoteRequest {
  source: 'customer_rate_quote';
  customer_contact_uuid?: string;
  rate_request: CustomerRateQuoteRequest;
  conditions_accepted: true;
  expected_total_amount_cents: number;
  expected_currency: string;
}

export interface SavedQuotePdf {
  filename: string;
  mime_type: 'application/pdf';
  template_version: string;
  quote_version: number;
  created_at: string;
  size_bytes: number;
  sha256: string;
}

export interface CurrentQuoteSummary {
  pdf?: SavedQuotePdf;
  quote_uuid: string;
  quote_number: number;
  display_quote_number: string;
  source: string;
  status: string;
  effective_status: string;
  customer_contact_uuid: string;
  customer_display_name: string | null;
  quote_date: string;
  effective_date: string;
  expiry_date: string;
  currency: string;
  total_amount_cents: number;
  origin_display: string | null;
  destination_display: string | null;
  conditions_accepted: boolean;
  version: number;
  created_at: string;
}

export interface CreateCurrentQuoteResponse {
  ok: boolean;
  resource: 'quote';
  data: CurrentQuoteSummary;
}

export interface CustomerSelectedRate {
  rate_method_label?: string | null;
  transit_time?: string | number | null;
  origin_zone?: ZoneResolution | null;
  destination_zone?: ZoneResolution | null;
  rating_basis?: 'mileage' | 'zone_rate' | string | null;
  base_rate_per_mile?: number | null;
  effective_rate_per_mile?: number | null;
  rate_multiplier?: number | null;
  trailer_space_feet?: number | null;
  distance?: {
    billable_miles?: number | null;
    calculated_miles?: number | null;
    distance_meters?: number | null;
  } | null;
  selected_equipment?: unknown;
}

export interface CustomerRateProduct {
  customer_product_uuid: string;
  customer_contact_uuid: string;
  product_model_number: string | null;
  product_name: string | null;
  product_description: string | null;
  package_description: string | null;
  product_barcode: string | null;
  product_class: string | null;
  shipping_class: string | null;
  shipping_length: number | null;
  shipping_width: number | null;
  shipping_height: number | null;
  shipping_weight: number | null;
  measurement_unit: number | null;
  measurement_unit_label: 'IN' | 'CM';
  weight_unit: number | null;
  weight_unit_label: 'LB' | 'KG';
  units_per_package: number | null;
  stackable: boolean | null;
  floor_rotation_allowed: boolean;
  packaging_type: number | null;
}

export type CustomerFreightPresentationMode =
  | 'generic'
  | 'catalog_equipment_linear_feet'
  | 'weight_only'
  | 'linear_feet_only'
  | 'cubic_weight_only'
  | 'density_only';

export interface CustomerFreightFieldVisibility {
  total_weight_lb: boolean;
  linear_feet: boolean;
  handling_units: boolean;
  cubic_feet: boolean;
  density_lb_per_cuft: boolean;
  delivery_type: boolean;
  load_type: boolean;
}

export interface CustomerFreightPresentation {
  configured: boolean;
  mode: CustomerFreightPresentationMode;
  profile_name: string | null;
  summary: string;
  customer_contact_uuid: string;
  preferred_rate_method: 1 | 2 | 3 | 4 | null;
  default_currency: string | null;
  show_package_builder: boolean;
  allow_catalog_products: boolean;
  allow_manual_packages: boolean;
  show_manual_metrics: boolean;
  show_rate_method_picker: boolean;
  field_visibility: CustomerFreightFieldVisibility;
  required_input: string;
  sources: {
    presentation: 'v_customer_freight_profiles' | 'v_contact_profiles' | 'default';
    rate_defaults: 'v_contact_profiles' | null;
  };
  profiles: {
    contact_profile_uuid: string | null;
    customer_freight_profile_uuid: string | null;
    strategy_name: string | null;
    profile_version: number | null;
  };
}

export interface CustomerRateProductsResponse {
  ok: boolean;
  resource: 'customer_rate_products';
  customer_contact_uuid: string;
  selection_mode: 'full_catalog' | 'autocomplete';
  total_products: number;
  minimum_search_characters: number;
  search_required: boolean;
  search: string | null;
  more_matches: boolean;
  products: CustomerRateProduct[];
  freight_presentation: CustomerFreightPresentation;
  meta: {
    scope: string;
    source: 'v_customer_products';
    returned: number;
    full_catalog_threshold: number;
    autocomplete_result_limit: number;
    pagination: false;
  };
}

export interface CustomerRoutePreviewRequest {
  origin: RateLocation;
  destination: RateLocation;
}

export interface CustomerRoutePreviewResponse {
  ok: boolean;
  success?: boolean;
  resource: 'customer_route_preview';
  origin_display: string;
  destination_display: string;
  distance_meters: number | null;
  distance_miles: number | null;
  distance_text: string;
  duration_seconds: number | null;
  duration_text: string;
  static_duration_seconds: number | null;
  static_duration_text: string;
  encoded_polyline?: string;
  map_image_data_url?: string;
  warnings?: string[];
  meta?: { source?: string };
}

export interface CustomerRateQuoteResponse {
  ok: boolean;
  resource: 'customer_rate_quote';
  status: CustomerRateQuoteStatus;
  quote_date: string;
  customer: {
    contact_uuid: string;
    display_name: string | null;
    contact_organization?: string | null;
    legal_name?: string | null;
  };
  lane: {
    origin: ResolvedLocation;
    destination: ResolvedLocation;
    origin_zone: ZoneResolution | null;
    destination_zone: ZoneResolution | null;
  };
  freight: Record<string, unknown>;
  selected_rate: CustomerSelectedRate | null;
  quote: {
    status: 'calculated' | 'not_calculable';
    currency: string | null;
    base_charge: unknown | null;
    total: CustomerRateMoney | null;
    line_items: CustomerRateLineItem[];
    notes: string[];
    conditions_version?: string;
    effective_date?: string | null;
    expiry_date?: string | null;
    conditions?: CustomerQuoteCondition[];
  };
  rate_candidates: unknown[];
  warnings: string[];
  meta: {
    source: 'live_db';
    scope: 'authenticated_customer';
    customer_only: true;
    components: string[];
    rating_basis?: 'mileage' | 'zone_rate' | string;
    candidate_count: number;
  };
}

@Injectable({ providedIn: 'root' })
export class CustomerRateQuoteApiService {

  constructor(private readonly http: HttpClient) {}

  quoteCustomerRate(payload: CustomerRateQuoteRequest): Observable<CustomerRateQuoteResponse> {
    return this.http.post<CustomerRateQuoteResponse>(
      '/api/rates/customer_rate_quote.php',
      payload
    );
  }

  createCurrentQuote(
    payload: CreateCurrentQuoteRequest,
    idempotencyKey: string
  ): Observable<CreateCurrentQuoteResponse> {
    return this.http.post<CreateCurrentQuoteResponse>(
      '/api/quotes/current_quotes.php',
      payload,
      {
        headers: new HttpHeaders({ 'Idempotency-Key': idempotencyKey })
      }
    );
  }

  downloadCurrentQuotePdf(quoteUuid: string): Observable<Blob> {
    return this.http.get('/api/quotes/current_quotes.php', {
      params: new HttpParams().set('format', 'pdf').set('quote_uuid', quoteUuid),
      responseType: 'blob'
    });
  }

  previewCustomerRoute(payload: CustomerRoutePreviewRequest): Observable<CustomerRoutePreviewResponse> {
    return this.http.post<CustomerRoutePreviewResponse>(
      '/api/rates/customer_route_preview.php',
      payload
    );
  }

  listCustomerShippingTargets(customerContactUuid?: string): Observable<CustomerShippingTargetsResponse> {
    let params = new HttpParams();
    if (customerContactUuid) {
      params = params.set('customer_contact_uuid', customerContactUuid);
    }

    return this.http.get<CustomerShippingTargetsResponse>(
      '/api/rates/customer_shipping_targets.php',
      { params }
    );
  }

  listCustomerProducts(customerContactUuid?: string, search?: string): Observable<CustomerRateProductsResponse> {
    let params = new HttpParams();
    if (customerContactUuid) {
      params = params.set('customer_contact_uuid', customerContactUuid);
    }
    if (search) {
      params = params.set('search', search);
    }

    return this.http.get<CustomerRateProductsResponse>(
      '/api/rates/customer_rate_products.php',
      { params }
    );
  }

}
